import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Plus, FolderOpen, Trash2, Archive, Copy, Loader2, Pencil } from 'lucide-react'
import { projectsApi, type Project } from '@/api/projects'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { formatDate } from '@/lib/utils'
import { useState } from 'react'

export default function ProjectList() {
  const queryClient = useQueryClient()
  const [duplicateProject, setDuplicateProject] = useState<Project | null>(null)
  const [dupName, setDupName] = useState('')
  const [dupCopyDocs, setDupCopyDocs] = useState(true)
  const [renameProject, setRenameProject] = useState<Project | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.list,
  })

  const deleteMutation = useMutation({
    mutationFn: projectsApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  })

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      projectsApi.update(id, { is_archived: archived }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  })

  const duplicateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; copy_documents?: boolean } }) =>
      projectsApi.duplicate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setDuplicateProject(null)
    },
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      projectsApi.update(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setRenameProject(null)
    },
  })

  const openRename = (project: Project) => {
    setRenameProject(project)
    setRenameValue(project.name)
  }

  const openDuplicate = (project: Project) => {
    setDuplicateProject(project)
    setDupName(`Copy of ${project.name}`)
    setDupCopyDocs(true)
  }

  const active = projects.filter((p) => !p.is_archived)
  const archived = projects.filter((p) => p.is_archived)

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Projects</h1>
          <p className="text-muted-foreground">{projects.length} total projects</p>
        </div>
        <Button asChild>
          <Link to="/projects/new">
            <Plus className="h-4 w-4 mr-1" />
            New Project
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading projects...</div>
      ) : active.length === 0 && archived.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-4">
              Create your first project to start extracting data.
            </p>
            <Button asChild>
              <Link to="/projects/new">Create Project</Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {active.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Active ({active.length})
              </h2>
              <div className="grid gap-3">
                {active.map((project) => (
                  <Card key={project.id} className="hover:border-primary/50 transition-colors">
                    <CardContent className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          <FolderOpen className="h-5 w-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <Link
                            to={`/projects/${project.id}`}
                            className="font-medium hover:text-primary truncate block"
                          >
                            {project.name}
                          </Link>
                          <p className="text-sm text-muted-foreground truncate">
                            {project.description || 'No description'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {project.document_count} docs · {project.field_count} fields · {formatDate(project.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 ml-4">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openRename(project)}
                          title="Rename project"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDuplicate(project)}
                          title="Duplicate project"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => archiveMutation.mutate({ id: project.id, archived: true })}
                          title="Archive"
                        >
                          <Archive className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm('Delete this project?')) {
                              deleteMutation.mutate(project.id)
                            }
                          }}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {archived.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Archived ({archived.length})
              </h2>
              <div className="grid gap-3 opacity-60">
                {archived.map((project) => (
                  <Card key={project.id}>
                    <CardContent className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <FolderOpen className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <span className="font-medium">{project.name}</span>
                          <Badge variant="secondary" className="ml-2 text-xs">Archived</Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => archiveMutation.mutate({ id: project.id, archived: false })}
                      >
                        Restore
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {/* Rename Dialog */}
      <Dialog open={!!renameProject} onOpenChange={(open) => { if (!open) setRenameProject(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Project Name</Label>
              <Input
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="Project name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && renameProject && renameValue.trim() && renameValue !== renameProject.name) {
                    renameMutation.mutate({ id: renameProject.id, name: renameValue.trim() })
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameProject(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (renameProject && renameValue.trim()) {
                  renameMutation.mutate({ id: renameProject.id, name: renameValue.trim() })
                }
              }}
              disabled={!renameValue.trim() || renameValue === renameProject?.name || renameMutation.isPending}
            >
              {renameMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Saving...</>
              ) : (
                'Save'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog open={!!duplicateProject} onOpenChange={(open) => { if (!open) setDuplicateProject(null) }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Duplicate Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>New Project Name</Label>
              <Input
                value={dupName}
                onChange={(e) => setDupName(e.target.value)}
                placeholder="Project name"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="copy-docs"
                checked={dupCopyDocs}
                onChange={(e) => setDupCopyDocs(e.target.checked)}
              />
              <Label htmlFor="copy-docs" className="cursor-pointer">
                Copy documents and annotations
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Fields and project settings are always copied. Extraction results are not copied.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateProject(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (duplicateProject) {
                  duplicateMutation.mutate({
                    id: duplicateProject.id,
                    data: { name: dupName, copy_documents: dupCopyDocs },
                  })
                }
              }}
              disabled={!dupName || duplicateMutation.isPending}
            >
              {duplicateMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-1" /> Duplicating...</>
              ) : (
                'Duplicate'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
